import { useEffect, useState } from 'react';
import { Building2, Search } from 'lucide-react';
import { CheckMark } from 'components/crm/ui/CheckRow';
import Spinner from 'components/crm/ui/Spinner';
import Tag from 'components/crm/ui/Tag';
import TextField from 'components/crm/ui/TextField';
import { foldText } from 'helper/foldText';
import RecordSourceSelect from 'components/record/RecordSourceSelect';
import useCompanyFilterOptions from 'hooks/useCompanyFilterOptions';
import useCrmContext from 'hooks/useCrmContext';
import useLatestCall from 'hooks/useLatestCall';
import useText from 'hooks/useText';
import { departmentLabel } from 'pages/companies/companiesItems';
import crmApi from 'server/crm/crmApi';
import { listProspects } from 'server/crm/prospectionApi';

// The search waits for the typing to pause before asking the api again.
const SEARCH_DELAY_MS = 300;

// Prospection calls: a company reached (« Répondu ») is followed elsewhere.
// The others come by what is most worth calling: a callback asked for, no
// answer yet, never called, then those not interested.
const RANK = { callback: 0, no_answer: 1, none: 2, not_interested: 3 };
const TAG_TONES = {
  callback: 'orange',
  no_answer: 'neutral',
  not_interested: 'danger',
};

// A prospect of the companies list with no commercial: never called by us.
const freeChoiceOf = (row) => ({
  companyId: String(row._id),
  company: {
    id: String(row._id),
    name: row.name_commercial || row.reason_social || '',
    city: departmentLabel(row.department),
  },
  source: 'free',
  last: null,
});

// The ids of one's companies in a department (the company list knows the
// departments, the prospect cards do not), every page of both tabs.
const ownCompaniesIn = async (call, meId, department) => {
  const ids = new Set();
  for (const tab of ['prospects', 'lost']) {
    let page = 1;
    let pages = 1;
    do {
      // eslint-disable-next-line no-await-in-loop
      const answer = await call(crmApi.listCompanies, {
        tab,
        page,
        filters: { commercial: meId, departments: [department] },
      });
      (answer?.companies ?? []).forEach((row) => ids.add(String(row._id)));
      pages = answer?.pages ?? 1;
      page += 1;
    } while (page <= pages);
  }
  return ids;
};

const matches = (company, words) =>
  !words ||
  foldText([company?.name, company?.city].filter(Boolean).join(' ')).includes(
    words,
  );

// The last call of each company among the calls made today.
const lastMadeOf = (made) => {
  const last = new Map();
  [...made]
    .sort((a, b) => (a.at < b.at ? -1 : 1))
    .forEach((call) => call.company?.id && last.set(call.company.id, call));
  return [...last.values()];
};

/**
 * The prospects one may call today, each to tick, under a search, never one
 * already reached (last call « Répondu »): first those called today, greyed
 * (`made`: they cannot be ticked nor unticked, a new attempt of the api may
 * keep them listed), then those of the list (`lines`), then one's own
 * prospects (listProspects, « Pas intéressé » ones included: adding one puts
 * it back to prospection), by RANK, then those of nobody (the companies list,
 * commercial « none », searched by the api). Each row: its name, its place,
 * the result of its last call. `selected`: the ids of the ticked companies;
 * `onToggle(choice)`, `choice` being { companyId, company, source: 'made' |
 * 'line' | 'mine' | 'free', line?, reactivate? }.
 */
const ProspectSelector = ({ lines, made = [], selected, onToggle }) => {
  const { text } = useText('prospection');
  const { call, ready, me } = useCrmContext();
  const latestFree = useLatestCall();
  const latestOwn = useLatestCall();
  const { departments } = useCompanyFilterOptions();
  // '' for every department; else one's own companies there (`inDepartment`).
  const [department, setDepartment] = useState('');
  const [inDepartment, setInDepartment] = useState(null);
  const [mine, setMine] = useState(null);
  const [free, setFree] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    listProspects().then(
      (answer) => setMine(answer?.prospects ?? []),
      () => setMine([]),
    );
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const timer = setTimeout(
      () =>
        latestFree(
          call(crmApi.listCompanies, {
            tab: 'prospects',
            search: query,
            filters: {
              commercial: 'none',
              departments: department ? [department] : [],
            },
          }),
        ).then(
          (page) => setFree((page?.companies ?? []).map(freeChoiceOf)),
          () => setFree([]),
        ),
      query ? SEARCH_DELAY_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [call, ready, latestFree, query, department]);

  const meId = me?.id;
  useEffect(() => {
    if (!department || !meId) {
      setInDepartment(null);
      return;
    }
    setInDepartment(undefined);
    latestOwn(ownCompaniesIn(call, meId, department)).then(
      setInDepartment,
      () => setInDepartment(new Set()),
    );
  }, [call, meId, department, latestOwn]);

  // The search, and the department beside it (Martinique, Guadeloupe…).
  const search = (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <TextField
          id="prospect-selector-search"
          type="search"
          icon={Search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher une entreprise"
          aria-label="Rechercher une entreprise"
        />
      </div>
      <RecordSourceSelect
        value={department || 'all'}
        allLabel="Tous les départements"
        sources={[
          ...new Map(
            departments.map((one) => [one.toLowerCase(), departmentLabel(one)]),
          ),
        ].map(([id, label]) => ({ id, label }))}
        onChange={(value) => setDepartment(value === 'all' ? '' : value)}
      />
    </div>
  );
  if (mine === null || free === null || inDepartment === undefined) {
    return (
      <div className="flex flex-col gap-3">
        {search}
        <div className="flex justify-center py-10" role="status">
          <Spinner />
        </div>
      </div>
    );
  }

  const words = foldText(query.trim());
  const rankOf = (choice) => RANK[choice.last ?? 'none'];
  const own = mine
    .map((row) => ({
      companyId: row.company.id,
      company: row.company,
      source: 'mine',
      last: row.lastCall?.result ?? null,
      reactivate: row.stage === 'lost' || row.prospectionStatus !== 'active',
    }))
    .filter((choice) => choice.last !== 'answered')
    .sort((a, b) => rankOf(a) - rankOf(b));
  // Each company once, in this order; the free ones come searched from the api.
  const seen = new Set();
  const choices = [
    ...lastMadeOf(made)
      .filter((one) => one.result !== 'answered')
      .map((one) => ({
        companyId: one.company.id,
        company: one.company,
        source: 'made',
        last: one.result,
      })),
    ...lines.map((line) => ({
      companyId: line.company?.id,
      company: line.company,
      source: 'line',
      line,
      last: null,
    })),
    ...own,
    ...free,
  ]
    .filter(
      (choice) =>
        choice.companyId &&
        !seen.has(choice.companyId) &&
        seen.add(choice.companyId),
    )
    .filter(
      (choice) => choice.source === 'free' || matches(choice.company, words),
    )
    // The free ones come filtered by the api; one's own by their companies there.
    .filter(
      (choice) =>
        choice.source === 'free' ||
        !inDepartment ||
        inDepartment.has(choice.companyId),
    );

  return (
    <div className="flex flex-col gap-3">
      {search}
      {!choices.length ? (
        <p className="py-4 text-sm text-muted">
          {words
            ? 'Aucune entreprise ne correspond à votre recherche.'
            : 'Aucun prospect à appeler pour le moment : ajoutez-en un.'}
        </p>
      ) : (
        <ul className="flex flex-col">
          {choices.map((choice) => {
            const checked = selected.has(choice.companyId);
            const called = choice.source === 'made';
            return (
              <li
                key={choice.companyId}
                className="border-b border-line last:border-b-0"
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  disabled={called}
                  onClick={() => onToggle(choice)}
                  className="flex w-full items-center gap-3 px-1 py-3 text-left transition-colors enabled:hover:bg-gray-50 disabled:cursor-default disabled:opacity-50"
                >
                  <CheckMark checked={checked} />
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-line bg-white text-ink">
                    <Building2 size={18} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-medium leading-5 text-ink">
                      {choice.company.name || '-'}
                    </span>
                    {(called || choice.company.city) && (
                      <span className="truncate text-xs leading-4 text-body">
                        {called ? 'Appelé aujourd’hui' : choice.company.city}
                      </span>
                    )}
                  </span>
                  {/* Why it is offered: the result of its last call, or a prospect never called. */}
                  {choice.source !== 'line' && (
                    <Tag tone={TAG_TONES[choice.last] ?? 'primary'} title>
                      {choice.last
                        ? text(`results.${choice.last}`)
                        : 'Jamais appelé'}
                    </Tag>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProspectSelector;
